<template>
  <v-container fluid>
    <!-- Page Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="52" class="mr-3 elevation-2">
            <v-icon color="white" size="28">mdi-bank-transfer</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">Cuadre de Afiliadas</div>
            <div class="text-caption grey--text">
              Cuentas por cobrar y pagar entre la compañía y sus afiliadas, según cada libro
            </div>
          </div>
        </div>
      </v-col>

      <!-- Action bar -->
      <v-col cols="auto" class="d-flex align-center no-print">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="grey darken-1" v-bind="attrs" v-on="on" class="mr-1"
              @click="print_report()" :disabled="!has_data">
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>Imprimir</span>
        </v-tooltip>

        <v-btn color="success" outlined class="mr-2 text-none"
          @click="downloadXLSX" :disabled="!has_data">
          <v-icon left small>mdi-file-excel-outline</v-icon>
          Excel
        </v-btn>

        <v-btn color="primary" outlined class="text-none" @click="show_filters = !show_filters">
          <v-icon left small>filter_alt</v-icon>
          Filtros
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-expand-transition>
      <v-card v-show="show_filters" outlined class="rounded-xl mb-4 pa-4 no-print">
        <v-row dense align="center">
          <v-col cols="12" md="3">
            <v-autocomplete v-model="filters.business_unit" :items="business_units" label="Unidad de negocio"
              outlined dense hide-details prepend-inner-icon="corporate_fare" class="rounded-lg"
              return-object @change="get_currencies()"></v-autocomplete>
          </v-col>

          <v-col cols="12" md="2">
            <v-menu v-model="menu_date" :close-on-content-click="false" transition="scale-transition"
              offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field :value="formatDate(filters.effective_date)" label="Fecha contable" outlined dense
                  readonly hide-details prepend-inner-icon="insert_invitation" class="rounded-lg"
                  v-bind="attrs" v-on="on"></v-text-field>
              </template>
              <v-date-picker v-model="filters.effective_date" no-title color="primary"
                @input="menu_date = false"></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="2">
            <v-autocomplete v-model="filters.currency" :items="currencies" label="Valores en"
              outlined dense hide-details item-text="code" return-object
              prepend-inner-icon="mdi-cash-multiple" class="rounded-lg"
              :disabled="!filters.business_unit"></v-autocomplete>
          </v-col>

          <v-col cols="12" md="2">
            <v-select v-model="filters.account_scope" :items="account_scopes" label="Cuentas"
              outlined dense hide-details class="rounded-lg" prepend-inner-icon="mdi-format-list-bulleted-type">
            </v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select v-model="filters.group_by" :items="group_options" label="Agrupar por"
              outlined dense hide-details class="rounded-lg" prepend-inner-icon="mdi-group"></v-select>
          </v-col>

          <v-col cols="12" md="1" class="d-flex justify-end">
            <v-btn color="success" depressed class="rounded-lg text-none font-weight-bold"
              :loading="loading" :disabled="!can_filter" @click="getData()">
              Generar
            </v-btn>
          </v-col>

          <v-col cols="12" class="pt-2 d-flex flex-wrap">
            <v-checkbox v-model="filters.include_zeros" hide-details dense class="mt-0 mr-6"
              label="Incluir afiliadas sin saldo en ninguno de los dos libros"></v-checkbox>
            <v-checkbox v-model="filters.show_unassigned" hide-details dense class="mt-0"
              label="Mostrar saldos no asignados a una afiliada"></v-checkbox>
          </v-col>
        </v-row>
      </v-card>
    </v-expand-transition>

    <!-- Report head (visible on print) -->
    <div v-if="applied.business_unit" class="mb-3">
      <div class="d-flex align-center">
        <div>
          <div class="text-subtitle-1 font-weight-bold">{{ applied.business_unit.description_long }}</div>
          <div class="text-caption grey--text text--darken-1">
            Cuentas por Cobrar y Pagar Afiliadas &mdash; Fecha contable: {{ formatDate(applied.effective_date) }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            Valores en {{ applied.currency ? applied.currency.code : '' }}
            &middot; {{ scope_label }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="text-caption grey--text no-print">{{ now }}</div>
      </div>
    </div>

    <!-- Table -->
    <v-card outlined class="rounded-xl">
      <div v-if="!has_data" class="pa-12 text-center">
        <v-icon size="64" color="grey lighten-1">mdi-bank-off-outline</v-icon>
        <div class="text-subtitle-1 grey--text mt-3">
          {{ applied.business_unit ? 'No hay saldos entre esta unidad y sus afiliadas' : 'Seleccione los filtros y genere el reporte' }}
        </div>
        <v-btn color="primary" outlined class="mt-4 text-none no-print" @click="show_filters = true">
          <v-icon left small>filter_alt</v-icon>
          Filtros
        </v-btn>
      </div>

      <div v-else class="table-wrapper">
        <table class="affiliates-table">
          <thead>
            <tr class="group-head">
              <th rowspan="2" class="text-left">Afiliada</th>
              <th colspan="3" class="text-center section-company">Según Compañía</th>
              <th colspan="3" class="text-center section-affiliate">Según Afiliada</th>
              <th colspan="3" class="text-center section-variance">Variación</th>
            </tr>
            <tr class="sub-head">
              <th class="text-right section-company">Ctas x Cobrar</th>
              <th class="text-right section-company">Ctas x Pagar</th>
              <th class="text-right section-company">Total</th>
              <th class="text-right section-affiliate">Ctas x Cobrar</th>
              <th class="text-right section-affiliate">Ctas x Pagar</th>
              <th class="text-right section-affiliate">Total</th>
              <th class="text-right section-variance">Ctas x Cobrar</th>
              <th class="text-right section-variance">Ctas x Pagar</th>
              <th class="text-right section-variance">Total</th>
            </tr>
          </thead>

          <tbody>
            <template v-for="group in groups">
              <tr :key="'g-' + group.key" class="group-title">
                <td colspan="10">{{ group.name }}</td>
              </tr>

              <tr v-for="item in group.items" :key="'r-' + group.key + '-' + item.business_unit_id">
                <td class="name-cell">
                  {{ item.description_long }}
                  <span v-if="item.status_id !== 1" class="inactive-tag">inactiva</span>
                </td>
                <td class="num">{{ money(item.company_receivable) }}</td>
                <td class="num">{{ money(item.company_payable) }}</td>
                <td class="num">{{ money(item.company_total) }}</td>
                <td class="num">{{ money(item.affiliate_receivable) }}</td>
                <td class="num">{{ money(item.affiliate_payable) }}</td>
                <td class="num">{{ money(item.affiliate_total) }}</td>
                <td class="num" :class="{ 'red--text': !is_zero(item.variance_receivable) }">
                  {{ money(item.variance_receivable) }}
                </td>
                <td class="num" :class="{ 'red--text': !is_zero(item.variance_payable) }">
                  {{ money(item.variance_payable) }}
                </td>
                <td class="num" :class="{ 'red--text': !is_zero(item.variance_total) }">
                  {{ money(item.variance_total) }}
                </td>
              </tr>

              <tr :key="'s-' + group.key" class="subtotal">
                <td class="name-cell">Sub Total</td>
                <td class="num">{{ money(group.totals.company_receivable) }}</td>
                <td class="num">{{ money(group.totals.company_payable) }}</td>
                <td class="num">{{ money(group.totals.company_total) }}</td>
                <td class="num">{{ money(group.totals.affiliate_receivable) }}</td>
                <td class="num">{{ money(group.totals.affiliate_payable) }}</td>
                <td class="num">{{ money(group.totals.affiliate_total) }}</td>
                <td class="num">{{ money(group.totals.variance_receivable) }}</td>
                <td class="num">{{ money(group.totals.variance_payable) }}</td>
                <td class="num">{{ money(group.totals.variance_total) }}</td>
              </tr>
            </template>
          </tbody>

          <tfoot>
            <tr class="grand-total">
              <td class="name-cell">Total afiliadas</td>
              <td class="num">{{ money(totals.company_receivable) }}</td>
              <td class="num">{{ money(totals.company_payable) }}</td>
              <td class="num">{{ money(totals.company_total) }}</td>
              <td class="num">{{ money(totals.affiliate_receivable) }}</td>
              <td class="num">{{ money(totals.affiliate_payable) }}</td>
              <td class="num">{{ money(totals.affiliate_total) }}</td>
              <td class="num">{{ money(totals.variance_receivable) }}</td>
              <td class="num">{{ money(totals.variance_payable) }}</td>
              <td class="num">{{ money(totals.variance_total) }}</td>
            </tr>

            <!-- Saldos que estan en las mismas cuentas pero no cuelgan de ninguna afiliada -->
            <template v-if="applied.show_unassigned && unassigned.length">
              <tr class="group-title">
                <td colspan="10">
                  No asignado a una afiliada
                  <span class="group-hint">
                    (entidades sin unidad de negocio y asientos sin entidad &mdash; revisar y reasignar)
                  </span>
                </td>
              </tr>
              <tr v-for="item in unassigned" :key="'u-' + (item.entity_id || 'null')">
                <td class="name-cell">
                  {{ item.entity_name }}
                  <span v-if="item.document_id" class="doc-tag">{{ item.document_id }}</span>
                </td>
                <td class="num">{{ money(item.company_receivable) }}</td>
                <td class="num">{{ money(item.company_payable) }}</td>
                <td class="num">{{ money(item.company_receivable - item.company_payable) }}</td>
                <td class="num muted">&mdash;</td>
                <td class="num muted">&mdash;</td>
                <td class="num muted">&mdash;</td>
                <td class="num muted">&mdash;</td>
                <td class="num muted">&mdash;</td>
                <td class="num muted">&mdash;</td>
              </tr>
              <tr class="subtotal">
                <td class="name-cell">Sub Total no asignado</td>
                <td class="num">{{ money(unassigned_totals.company_receivable) }}</td>
                <td class="num">{{ money(unassigned_totals.company_payable) }}</td>
                <td class="num">{{ money(unassigned_totals.company_total) }}</td>
                <td class="num muted" colspan="6">&mdash;</td>
              </tr>
              <tr class="grand-total">
                <td class="name-cell">Total cuentas en el mayor</td>
                <td class="num">{{ money(totals.company_receivable + unassigned_totals.company_receivable) }}</td>
                <td class="num">{{ money(totals.company_payable + unassigned_totals.company_payable) }}</td>
                <td class="num">{{ money(totals.company_total + unassigned_totals.company_total) }}</td>
                <td class="num muted" colspan="6">&mdash;</td>
              </tr>
            </template>
          </tfoot>
        </table>
      </div>
    </v-card>

    <div class="text-caption grey--text mt-2">
      La variación de "Ctas x Cobrar" compara lo que la compañía tiene por pagar contra lo que la afiliada
      tiene por cobrar; la de "Ctas x Pagar" compara lo que la compañía tiene por cobrar contra lo que la
      afiliada tiene por pagar.
    </div>
  </v-container>
</template>

<script>
import moment from "moment";
import ExcelJS from "exceljs";

const EMPTY_TOTALS = () => ({
  company_receivable: 0,
  company_payable: 0,
  company_total: 0,
  affiliate_receivable: 0,
  affiliate_payable: 0,
  affiliate_total: 0,
  variance_receivable: 0,
  variance_payable: 0,
  variance_total: 0,
});

export default {
  name: "AffiliatesBalance",
  data: () => ({
    show_filters: true,
    loading: false,
    menu_date: false,
    currencies: [],
    rows: [],
    unassigned: [],
    applied: {},
    filters: {
      business_unit: null,
      effective_date: moment().format("YYYY-MM-DD"),
      currency: null,
      account_scope: 1,
      group_by: "consortium",
      include_zeros: false,
      show_unassigned: true,
    },
    account_scopes: [
      { text: "Solo cuentas afiliadas", value: 1 },
      { text: "Todas las CxC / CxP", value: 2 },
    ],
    group_options: [
      { text: "Consorcio", value: "consortium" },
      { text: "Compañía", value: "company" },
      { text: "Sin agrupar", value: "none" },
    ],
  }),
  mounted() {
    this.$root.$emit("toggle-mini", true);
  },
  computed: {
    business_units() {
      return this.$store.state.auth.bussines_units;
    },
    can_filter() {
      return this.filters.business_unit != null && this.filters.currency != null;
    },
    has_data() {
      return this.rows.length > 0 || this.unassigned.length > 0;
    },
    now() {
      return moment().format("DD/MM/YYYY hh:mm A");
    },
    scope_label() {
      const scope = this.account_scopes.find((s) => s.value === this.applied.account_scope);
      return scope ? scope.text : "";
    },
    groups() {
      const grouped = [];
      const index = {};

      for (const item of this.rows) {
        const key = this.group_key(item);
        if (index[key] === undefined) {
          index[key] = grouped.length;
          grouped.push({ key, name: this.group_name(item), items: [], totals: EMPTY_TOTALS() });
        }
        const group = grouped[index[key]];
        group.items.push(item);
        this.accumulate(group.totals, item);
      }
      return grouped;
    },
    totals() {
      const totals = EMPTY_TOTALS();
      for (const item of this.rows) {
        this.accumulate(totals, item);
      }
      return totals;
    },
    unassigned_totals() {
      const totals = EMPTY_TOTALS();
      for (const item of this.unassigned) {
        totals.company_receivable += item.company_receivable;
        totals.company_payable += item.company_payable;
      }
      totals.company_total = totals.company_receivable - totals.company_payable;
      return totals;
    },
  },
  methods: {
    formatDate(date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
    money(value) {
      const amount = Number(value) || 0;
      return amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    is_zero(value) {
      return Math.abs(Number(value) || 0) < 0.005;
    },
    group_key(item) {
      if (this.applied.group_by === "company") return "c" + item.company_id;
      if (this.applied.group_by === "none") return "all";
      return "k" + item.consortium_id;
    },
    group_name(item) {
      if (this.applied.group_by === "company") return item.company_name || "Sin compañía";
      if (this.applied.group_by === "none") return "Afiliadas";
      return item.consortium_name || "Sin consorcio";
    },
    accumulate(totals, item) {
      totals.company_receivable += item.company_receivable;
      totals.company_payable += item.company_payable;
      totals.company_total += item.company_total;
      totals.affiliate_receivable += item.affiliate_receivable;
      totals.affiliate_payable += item.affiliate_payable;
      totals.affiliate_total += item.affiliate_total;
      totals.variance_receivable += item.variance_receivable;
      totals.variance_payable += item.variance_payable;
      totals.variance_total += item.variance_total;
    },
    async get_currencies() {
      this.filters.currency = null;
      this.currencies = [];
      if (this.filters.business_unit == null) return;
      try {
        const result = await this.api_Post("accounting/currenciesBusiness", {
          business: this.filters.business_unit.id,
          effective_date: this.filters.effective_date,
        });
        this.currencies = await result.json();
        this.filters.currency =
          this.currencies.find((element) => element.id == this.filters.business_unit.currency_id) ||
          this.currencies.find((element) => element.default_currency_id == element.id) ||
          this.currencies[0];
      } catch (error) {
        console.error(error.message);
      }
    },
    async getData() {
      this.loading = true;
      try {
        const result = await this.api_Post("accounting/affiliates_balance", {
          business_unit_id: this.filters.business_unit.id,
          effective_date: this.filters.effective_date,
          currency_id: this.filters.currency.id,
          // El alcance amplio suma todas las cuentas CxC/CxP (externo, afiliado, relacionado, empleado)
          entity_type_ids: this.filters.account_scope == 1 ? [2] : [1, 2, 3, 4],
          include_zeros: this.filters.include_zeros,
        });


        if (result.status != 200) {
          this.$toast.error("No se pudo generar el reporte");
          return;
        }

        const data = await result.json();
        this.unassigned = this.filters.show_unassigned ? data.unassigned || [] : [];
        this.rows = (data.rows || []).map((item) => {
          const company_total = item.company_receivable - item.company_payable;
          const affiliate_total = item.affiliate_receivable - item.affiliate_payable;
          const variance_receivable = item.company_payable - item.affiliate_receivable;
          const variance_payable = item.company_receivable - item.affiliate_payable;
          return {
            ...item,
            company_total,
            affiliate_total,
            variance_receivable,
            variance_payable,
            variance_total: variance_receivable - variance_payable,
          };
        });
        this.applied = Object.assign({}, this.filters);
        this.show_filters = false;
        this.$root.$emit("toggle-mini", true);
      } catch (error) {
        console.error(error.message);
        this.rows = [];
        this.unassigned = [];
        this.$toast.error("No se pudo generar el reporte");
      } finally {
        this.loading = false;
      }
    },
    print_report() {
      window.print();
    },
    async downloadXLSX() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Cuadre de afiliadas");
      const accountingFormat = '_(* #,##0.00_);_(* \\(#,##0.00\\);_(* "-"??_);_(@_)';

      worksheet.columns = [
        { key: "name", width: 40 },
        { key: "c_cxc", width: 16 },
        { key: "c_cxp", width: 16 },
        { key: "c_total", width: 16 },
        { key: "a_cxc", width: 16 },
        { key: "a_cxp", width: 16 },
        { key: "a_total", width: 16 },
        { key: "v_cxc", width: 16 },
        { key: "v_cxp", width: 16 },
        { key: "v_total", width: 16 },
      ];

      const title = worksheet.addRow([this.applied.business_unit.description_long]);
      title.getCell(1).font = { size: 14, bold: true };
      worksheet.addRow(["Cuentas por Cobrar y Pagar Afiliadas"]).getCell(1).font = { bold: true };
      worksheet.addRow([`Fecha contable: ${this.formatDate(this.applied.effective_date)}`]);
      worksheet.addRow([
        `Valores en ${this.applied.currency.code} — ${this.scope_label}`,
      ]);
      worksheet.addRow([]);

      const sectionRow = worksheet.addRow([
        "", "Según Compañía", "", "", "Según Afiliada", "", "", "Variación", "", "",
      ]);
      worksheet.mergeCells(`B${sectionRow.number}:D${sectionRow.number}`);
      worksheet.mergeCells(`E${sectionRow.number}:G${sectionRow.number}`);
      worksheet.mergeCells(`H${sectionRow.number}:J${sectionRow.number}`);
      [2, 5, 8].forEach((col) => {
        sectionRow.getCell(col).alignment = { horizontal: "center" };
        sectionRow.getCell(col).font = { bold: true };
      });

      const headerRow = worksheet.addRow([
        "Afiliada",
        "Ctas x Cobrar", "Ctas x Pagar", "Total",
        "Ctas x Cobrar", "Ctas x Pagar", "Total",
        "Ctas x Cobrar", "Ctas x Pagar", "Total",
      ]);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      });

      const addAmountRow = (label, values, bold) => {
        const row = worksheet.addRow([
          label,
          values.company_receivable, values.company_payable, values.company_total,
          values.affiliate_receivable, values.affiliate_payable, values.affiliate_total,
          values.variance_receivable, values.variance_payable, values.variance_total,
        ]);
        row.eachCell((cell, colNumber) => {
          if (colNumber > 1) cell.numFmt = accountingFormat;
          if (bold) cell.font = { bold: true };
        });
        return row;
      };

      for (const group of this.groups) {
        const groupRow = worksheet.addRow([group.name]);
        groupRow.getCell(1).font = { bold: true };
        for (const item of group.items) {
          addAmountRow(
            item.status_id === 1 ? item.description_long : `${item.description_long} (inactiva)`,
            item
          );
        }
        addAmountRow("Sub Total", group.totals, true);
        worksheet.addRow([]);
      }

      const totalRow = addAmountRow("Total afiliadas", this.totals, true);
      totalRow.eachCell((cell, colNumber) => {
        if (colNumber > 1) cell.border = { bottom: { style: "double", color: { argb: "black" } } };
      });

      if (this.applied.show_unassigned && this.unassigned.length) {
        worksheet.addRow([]);
        const head = worksheet.addRow([
          "No asignado a una afiliada (entidades sin unidad de negocio y asientos sin entidad)",
        ]);
        head.getCell(1).font = { bold: true };
        for (const item of this.unassigned) {
          const row = worksheet.addRow([
            item.document_id ? `${item.entity_name} (${item.document_id})` : item.entity_name,
            item.company_receivable,
            item.company_payable,
            item.company_receivable - item.company_payable,
          ]);
          row.eachCell((cell, colNumber) => {
            if (colNumber > 1) cell.numFmt = accountingFormat;
          });
        }
        const subtotal = worksheet.addRow([
          "Sub Total no asignado",
          this.unassigned_totals.company_receivable,
          this.unassigned_totals.company_payable,
          this.unassigned_totals.company_total,
        ]);
        subtotal.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          if (colNumber > 1) cell.numFmt = accountingFormat;
        });
        const mayor = worksheet.addRow([
          "Total cuentas en el mayor",
          this.totals.company_receivable + this.unassigned_totals.company_receivable,
          this.totals.company_payable + this.unassigned_totals.company_payable,
          this.totals.company_total + this.unassigned_totals.company_total,
        ]);
        mayor.eachCell((cell, colNumber) => {
          cell.font = { bold: true };
          if (colNumber > 1) {
            cell.numFmt = accountingFormat;
            cell.border = { bottom: { style: "double", color: { argb: "black" } } };
          }
        });
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Cuadre de afiliadas ${this.applied.business_unit.description_short} al ${this.applied.effective_date}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: auto;
}

.affiliates-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.affiliates-table th,
.affiliates-table td {
  padding: 6px 10px;
  white-space: nowrap;
}

.affiliates-table thead th {
  background: #fafafa;
  font-weight: 700;
  color: #616161;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.4px;
  border-bottom: 1px solid #e0e0e0;
}

.group-head th.section-company { border-left: 1px solid #e0e0e0; }
.group-head th.section-affiliate { border-left: 1px solid #e0e0e0; }
.group-head th.section-variance { border-left: 1px solid #e0e0e0; }
.sub-head th:nth-child(1),
.sub-head th:nth-child(4),
.sub-head th:nth-child(7) { border-left: 1px solid #e0e0e0; }

.affiliates-table tbody tr:hover { background: #fafafa; }

.group-title td {
  font-weight: 700;
  color: #424242;
  background: #f5f5f5;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #eeeeee;
}

.name-cell {
  text-align: left;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.num.muted {
  color: #bdbdbd;
}

.inactive-tag,
.doc-tag {
  display: inline-block;
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 3px;
  background: #eeeeee;
  color: #757575;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  vertical-align: middle;
}

.group-hint {
  font-weight: 400;
  text-transform: none;
  color: #9e9e9e;
  font-size: 11px;
}

.subtotal td {
  font-weight: 700;
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
}

.grand-total td {
  font-weight: 800;
  border-top: 2px solid #9e9e9e;
  border-bottom: 3px double #9e9e9e;
}

@media print {
  .affiliates-table { font-size: 8px; }
  .affiliates-table th,
  .affiliates-table td { padding: 2px 4px; }
  .table-wrapper { overflow: visible; }
}
</style>
